/**
 * Story layer (docs/IDEAS-BACKLOG.md §E): registry of every world's `story.ts` + the pure selection logic the client
 * uses for meet sheets, narrated steps, reactions, chapter comics, the child's own storybook and bedtime mode.
 */
import { localize, type L, type Lang } from '../text';
import { pickLocale, type LocaleTable } from '../i18n/locales';
import type { PathId } from '../content/model';
import { PATH_IDS } from '../content/model';
import type { StoryCastMember, StoryChapterComic, StoryComicPanel, StoryMeet, WorldStoryLayer } from './types';

export * from './types';

/**
 * Registered story layers, world order = map order. Filled by `./all` (server, tools, tests: every bundled world) or,
 * on the client, world by world as the world's content chunk loads (`registerStoryLayer`) — the layers are pure data.
 */
export const STORY_LAYERS: WorldStoryLayer[] = [];
const BY_WORLD = new Map<string, WorldStoryLayer>();

/** Add (or replace) a world's story layer, keeping map order. */
export function registerStoryLayer(layer: WorldStoryLayer): void {
  BY_WORLD.set(layer.world, layer);
  const order = (w: string) => {
    const i = (PATH_IDS as readonly string[]).indexOf(w);
    return i < 0 ? 99 : i;
  };
  STORY_LAYERS.splice(0, STORY_LAYERS.length, ...[...BY_WORLD.values()].sort((a, b) => order(a.world) - order(b.world)));
}

export const storyForWorld = (world: string | undefined | null): WorldStoryLayer | undefined => (world ? BY_WORLD.get(world) : undefined);

export function castMember(world: string, castId: string): StoryCastMember | undefined {
  return storyForWorld(world)?.cast.find((c) => c.id === castId);
}

/** Global key of a cast member (cast ids are only unique inside a world). */
export const castKey = (world: string, castId: string) => `${world}:${castId}`;

/** Which world's story owns this lesson (narration or a cast member drawn by it). */
export function storyWorldOf(drawingId: string): string | undefined {
  for (const s of STORY_LAYERS) if (s.stepNarration[drawingId] || s.cast.some((c) => c.drawingId === drawingId)) return s.world;
  return undefined;
}

/** The cast member a lesson features: its narrator if any, else the character the drawing IS. */
export function featuredCast(drawingId: string): { world: string; cast: StoryCastMember } | undefined {
  for (const s of STORY_LAYERS) {
    const id = s.stepNarration[drawingId]?.castId ?? s.cast.find((c) => c.drawingId === drawingId)?.id;
    const cast = id ? s.cast.find((c) => c.id === id) : undefined;
    if (cast) return { world: s.world, cast };
  }
  return undefined;
}

/**
 * The meet sheet to show before a lesson: the featured character's "hello" when the child has not met them yet.
 * The host is never "met" (they guide the whole world). `met` holds castKey()s.
 */
export function meetForLesson(drawingId: string, met: readonly string[], opts: { again?: boolean } = {}): { world: string; cast: StoryCastMember; meet: StoryMeet } | undefined {
  const f = featuredCast(drawingId);
  if (!f) return undefined;
  const s = storyForWorld(f.world)!;
  if (f.cast.id === s.host || (!opts.again && met.includes(castKey(f.world, f.cast.id)))) return undefined;
  const meet = s.meet.find((m) => m.castId === f.cast.id && m.drawingId === drawingId) ?? s.meet.find((m) => m.castId === f.cast.id);
  return meet ? { world: f.world, cast: f.cast, meet } : undefined;
}

/** A cast member's meet sheet (replay from Collect → Residents); undefined for the host (no hello sheet). */
export function meetForCast(world: string, castId: string): { world: string; cast: StoryCastMember; meet: StoryMeet } | undefined {
  const s = storyForWorld(world);
  const cast = s?.cast.find((c) => c.id === castId);
  const meet = s?.meet.find((m) => m.castId === castId);
  return s && cast && meet ? { world, cast, meet } : undefined;
}

/** "Story before lessons" setting: every time, only the first time a character appears, or never on its own. */
export type StoryOfferMode = 'always' | 'first' | 'off';
export const STORY_OFFER_MODES: StoryOfferMode[] = ['always', 'first', 'off'];
export const DEFAULT_STORY_OFFER: StoryOfferMode = 'first';
/** After this many "Draw now" picks in a row the meet card stops offering itself (the 📖 button stays). */
export const STORY_SKIP_LIMIT = 3;

/**
 * Should a lesson open with the meet card? `mode` = the profile's setting, `skips` = "Draw now" picks in a row.
 * Returns the meet to offer, or undefined (the caller still files the Character Card quietly).
 */
export function storyOffer(drawingId: string, met: readonly string[], mode: StoryOfferMode, skips: number): { world: string; cast: StoryCastMember; meet: StoryMeet } | undefined {
  if (mode === 'off' || skips >= STORY_SKIP_LIMIT) return undefined;
  return meetForLesson(drawingId, met, { again: mode === 'always' });
}

/** The story line for one step of a lesson (falls back to undefined → the technical caption). */
export function narrationFor(drawingId: string, stepIndex: number): { world: string; castId: string; line: L } | undefined {
  for (const s of STORY_LAYERS) {
    const n = s.stepNarration[drawingId];
    if (n && n.lines[stepIndex]) return { world: s.world, castId: n.castId, line: n.lines[stepIndex] };
  }
  return undefined;
}

/** Small deterministic hash (FNV-1a) so a reaction stays the same for one drawing but varies across drawings. */
export function storyHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** One reaction line of the featured character (or the world host) to the child's drawing. */
export function reactionFor(drawingId: string, seed: string): { world: string; cast: StoryCastMember; line: L } | undefined {
  const f = featuredCast(drawingId);
  if (!f) return undefined;
  const s = storyForWorld(f.world)!;
  const lines = s.reactions.find((r) => r.castId === f.cast.id)?.lines ?? s.reactions.find((r) => r.castId === s.host)?.lines;
  if (!lines?.length) return undefined;
  return { world: f.world, cast: f.cast, line: lines[storyHash(`${drawingId}|${seed}`) % lines.length]! };
}

export function chapterComic(world: string, level: number, when: 'start' | 'end'): StoryChapterComic | undefined {
  return storyForWorld(world)?.chapterComics.find((c) => c.level === level && c.when === when);
}

/** Every comic of a world in reading order (L1 start, L1 end, … L4 end). */
export function comicsInOrder(world: string): StoryChapterComic[] {
  const s = storyForWorld(world);
  if (!s) return [];
  const order = (c: StoryChapterComic) => c.level * 2 + (c.when === 'end' ? 1 : 0);
  return [...s.chapterComics].sort((a, b) => order(a) - order(b));
}

/* ------------------------------------------- "My story" storybook ------------------------------------------- */

/** A drawing the child made (journal photo or living cut-out), keyed by the lesson it came from. */
export interface ChildArt {
  drawingId: string;
  /** Opaque reference for the client (journal entry id / creature id). */
  ref: string;
  at: number;
}

export interface StorybookPanel {
  panel: StoryComicPanel;
  /** The child's drawings placed in this panel (matched by the panel's drawings or its cast's drawings). */
  art: ChildArt[];
}

export type StorybookPage =
  | { kind: 'cover'; world: string; title: L }
  | { kind: 'chapter'; level: number; when: 'start' | 'end'; title: L; panels: StorybookPanel[] }
  | { kind: 'finale'; title: L; panels: StorybookPanel[] }
  | { kind: 'end'; world: string; count: number };

/** Drawings that stand for a panel: its own drawingIds + the drawing of every cast member on stage. */
export function panelDrawingIds(world: string, panel: StoryComicPanel): string[] {
  const s = storyForWorld(world);
  const ids = [...(panel.drawingIds ?? [])];
  for (const c of panel.cast) {
    const d = s?.cast.find((x) => x.id === c)?.drawingId;
    if (d && !ids.includes(d)) ids.push(d);
  }
  return ids;
}

/**
 * Compose the child's own storybook for a world: cover → every chapter comic (L4 end = the finale double page) → the end.
 * The newest child drawing of each lesson is placed into the panels that show it (max 2 per panel).
 * Returns null when the child has no drawing of this world yet.
 */
export function composeStorybook(world: string, art: readonly ChildArt[]): StorybookPage[] | null {
  const s = storyForWorld(world);
  if (!s || !s.chapterComics.length) return null;
  const newest = new Map<string, ChildArt>();
  for (const a of art) {
    const cur = newest.get(a.drawingId);
    if (!cur || a.at > cur.at) newest.set(a.drawingId, a);
  }
  const pages: StorybookPage[] = [{ kind: 'cover', world, title: s.title }];
  let placed = 0;
  const used = new Set<string>();
  for (const c of comicsInOrder(world)) {
    const panels = c.panels.map((panel) => {
      const mine = panelDrawingIds(world, panel).map((id) => newest.get(id)).filter((x): x is ChildArt => !!x).slice(0, 2);
      for (const m of mine) used.add(m.ref);
      placed += mine.length;
      return { panel, art: mine };
    });
    const finale = c.level === 4 && c.when === 'end';
    pages.push(finale ? { kind: 'finale', title: c.title, panels } : { kind: 'chapter', level: c.level, when: c.when, title: c.title, panels });
  }
  if (!placed) {
    const own = [...newest.values()].filter((a) => s.stepNarration[a.drawingId] || s.cast.some((c) => c.drawingId === a.drawingId));
    if (!own.length) return null;
  }
  pages.push({ kind: 'end', world, count: used.size });
  return pages;
}

/** Worlds the child can open a storybook for (has at least one drawing of the world's story). */
export function storybookWorlds(art: readonly ChildArt[]): string[] {
  const ids = new Set(art.map((a) => a.drawingId));
  return STORY_LAYERS.filter((s) => Object.keys(s.stepNarration).some((id) => ids.has(id)) || s.cast.some((c) => c.drawingId && ids.has(c.drawingId))).map((s) => s.world);
}

/* ------------------------------------------- bedtime mode ------------------------------------------- */

/** Seconds a bedtime page stays up: reading time of the captions at a slow bedtime pace, clamped. */
export function bedtimePageSeconds(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(6, Math.min(22, Math.round(words / 1.6) + 3));
}

export const SLEEP_TIMER_MINUTES = [5, 10, 15] as const;

/** All the text Juno reads aloud on one storybook page. */
const THE_END: LocaleTable<string> = { en: 'The end. Sleep tight.', vi: 'Hết rồi. Chúc con ngủ ngon.', 'zh-Hans': '故事讲完啦。晚安，好梦。' };
export function pageNarration(page: StorybookPage, lang: Lang): string {
  if (page.kind === 'cover') return localize(page.title, lang);
  if (page.kind === 'end') return pickLocale(THE_END, lang);
  return [localize(page.title, lang), ...page.panels.map((p) => localize(p.panel.caption, lang))].join(' ');
}

/** Reached by `met` ids on the profile: Character Cards in the Creature book "Residents" tab, in world/cast order. */
export function residentCards(met: readonly string[]): { world: string; cast: StoryCastMember; key: string }[] {
  const out: { world: string; cast: StoryCastMember; key: string }[] = [];
  for (const s of STORY_LAYERS) for (const c of s.cast) if (met.includes(castKey(s.world, c.id))) out.push({ world: s.world, cast: c, key: castKey(s.world, c.id) });
  return out;
}

export const storyWorlds = (): PathId[] => STORY_LAYERS.map((s) => s.world as PathId);
